/// <reference path="../../../global.d.ts" />

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslatedDatepickerModule } from '@jfw/angular/shared/i18n';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { PhonePipe } from '@jfw/angular/shared/pipes';
import { convertLegacyDateToUnixTimestamp } from '@jfw/business-logic';
import {
  EventListItem,
  EzTuxEnvironment,
  EzTuxParentApplication,
  RejectAcceptEventRequest,
} from '@jfw/types';
import { Subscription } from 'rxjs';
import { WholesalePotentialEventService } from './potential-event.service';
import { WholesalePotentialEventsRejectionModalComponent } from './rejection-modal/rejection-modal.component';

// moved to jfw-library/global.d.ts
// declare global {
//   interface Window {
//     acceptEvent: (webeventid: string) => void;
//     isCSRMode: () => string;
//   }
// }

@Component({
  selector: 'jfw-wholesale-potential-events-list-view',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslatedDatepickerModule,
    MatCheckboxModule,
    MatTableModule,
    MatProgressSpinnerModule,
    CommonModule,
    MatIconModule,
    PhonePipe,
  ],
  templateUrl: './potential-events-list-view.component.html',
  styleUrl: './potential-events-list-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WholesalePotentialEventsListViewComponent {
  potentialEventService = inject(WholesalePotentialEventService);
  @Input() receivedEventList: EventListItem[] | undefined = [];
  @Input() eztux_base_url: string = 'https://ezdevbo-1.jimsfw.com';
  @Input() set platform_event_api_server_url(url: string) {
    this.potentialEventService.setPlatformEventApiUrl(url);
    console.log('Set platform_event_api_server_url:', url);
  }
  @Input() parent_application: EzTuxParentApplication;
  @Input() environment: EzTuxEnvironment;
  @Input() newSite = false;

  // Allowlist (default-deny), matching the pattern already established by
  // WholesaleOrderListViewComponent.redirectToReceipt() — an unrecognized or
  // unset parent_application (including the KMS/eztux embed) always falls
  // through to the legacy design.
  get showNewDesign(): boolean {
    return (
      this.parent_application === 'jfwplatformportal' ||
      (this.parent_application === 'dealerportal' && this.newSite)
    );
  }

  dialogSubscription: Subscription | undefined;
  // EXT-6593: signals, not plain fields -- all three are read directly in
  // the template and written from .subscribe()/.then()/.catch()/setTimeout()
  // callbacks, none of which are template-bound events.
  saving = signal(false);
  saved = signal(false);

  expandedElements = new Set<EventListItem>();

  loading = false;
  loaded = signal(true);
  error = signal('');
  form = new FormGroup({
    accountNumber: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    fromDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    toDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  private eventListSubscription: Subscription | undefined;

  eventListWrapper$ = this.potentialEventService.eventListWrapper$;

  constructor(private dialog: MatDialog) {}

  ngOnInit() {
    console.log('PotentialEvents parent_application:', this.parent_application);
    console.log('PotentialEvents environment:', this.environment);
    this.eventListSubscription =
      this.potentialEventService.eventListWrapper$.subscribe((wrapper) => {
        if (wrapper.status === 'PENDING') {
          this.loaded.set(false);
          this.error.set('');
        } else if (wrapper.status === 'READY') {
          this.loaded.set(true);
          this.error.set('');
          if (
            Array.isArray(wrapper.eventList) &&
            wrapper.eventList.every((item) => 'omniId' in item)
          ) {
            this.receivedEventList = wrapper.eventList as EventListItem[];
          } else {
            this.receivedEventList = undefined;
          }
        } else if (wrapper.status === 'ERROR') {
          this.loaded.set(true);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
    if (this.eventListSubscription) {
      this.eventListSubscription.unsubscribe();
    }
  }

  // Add 'expand' column to the beginning
  displayedColumns = [
    'expand',
    'eventDate',
    'eventName',
    'organizerName',
    'sharedDate',
  ] as const;

  selectedEventDisplayedColumns = [
    'customer',
    'role',
    'coat',
    'pant',
    'shirt',
    'tie',
    'vest',
    'shoe',
    'pocketSquare',
    'jewelry',
  ];

  isExpanded(element: EventListItem): boolean {
    return this.expandedElements.has(element);
  }

  onEventClick(event: EventListItem): void {
    if (this.expandedElements.has(event)) {
      this.expandedElements.delete(event);
    } else {
      this.expandedElements.add(event);
    }
    this.saved.set(false);
    this.saving.set(false);
  }

  formatDate(date: string): number {
    return convertLegacyDateToUnixTimestamp(date);
  }

  onAcceptEvent(eventListItem: EventListItem, event: Event) {
    event.stopPropagation();
    console.log('Accepting event:', eventListItem);

    // Set cookie to refresh data
    const maxAge = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    const expiresTS = Date.now() + maxAge;
    const expires = new Date(expiresTS).toUTCString();
    document.cookie = `refreshDataPods=${expiresTS}; path=/; expires=${expires}`;

    console.log('Setting cookie refreshDataPods:', expires);

    // setTimeout(() => {
    return window.acceptEvent(eventListItem.omniId);

    //}, 250);
    //

    // setTimeout(() => {
    //   this.potentialEventService.triggerFetchData(true);
    // }, 1500);
    // return false;
    // const rejectEventRequest: RejectAcceptEventRequest = {
    //   eventId: eventListItem.id,
    //   isRejection: false,
    // };
    // this.saving = true;
    // this.potentialEventService
    //   .apiRejectAcceptEvent(rejectEventRequest)
    //   .then((response) => {
    //     console.log('Event accepted successfully:', response);
    //     this.saving = false;
    //     this.saved = true;
    //   });
  }

  onRejectEvent(eventListItem: EventListItem) {
    this.openRejectionReasonModal(eventListItem);
    return false;
  }

  async openRejectionReasonModal(eventListItem: EventListItem) {
    const dialogRef = this.dialog.open(
      WholesalePotentialEventsRejectionModalComponent,
      {
        width: '400px',
        disableClose: true,
        data: { showNewDesign: this.showNewDesign },
      },
    );

    this.dialogSubscription = dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.saving.set(true);
        const rejectEventRequest: RejectAcceptEventRequest = {
          eventId: eventListItem.id,
          isRejection: true,
          rejectCode: result.code,
          rejectDescription: result.description,
        };

        this.potentialEventService
          .apiRejectEvent(rejectEventRequest)
          .then((response) => {
            console.log('Event rejected successfully:', response);

            /// Adding a 5 second delay to allow syncing
            setTimeout(() => {
              this.saving.set(false);
              this.saved.set(true);
              this.potentialEventService.triggerFetchData(true);
            }, 5000);
          })
          .catch((err) => {
            // Without this, a failed request (e.g. a misconfigured
            // platform_event_api_server_url) left `saving` stuck true
            // forever -- the spinner never cleared and Accept/Reject never
            // came back, with no way to retry.
            console.error('Event rejection failed:', err);
            this.saving.set(false);
            this.saved.set(false);
          });
      } else {
        this.saving.set(false);
        this.saved.set(false);
      }
    });
  }
}
